"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useDimensions = exports.observerErr = exports.borderBoxWarn = void 0;
var _react = require("react");
var _browser = require("../utils/browser");
const observerErr = "💡 react-cool-dimensions: the browser doesn't support Resize Observer, please use polyfill: https://github.com/wellyshen/react-cool-dimensions#resizeobserver-polyfill";
exports.observerErr = observerErr;
const borderBoxWarn = "💡 react-cool-dimensions: the browser doesn't support border-box size, fallback to content-box size. Please see: https://github.com/wellyshen/react-cool-dimensions#border-box-size-measurement";
exports.borderBoxWarn = borderBoxWarn;
const getCurrentBreakpoint = (bps, width) => {
  let curBp = '';
  let max = -1;
  Object.keys(bps).forEach(key => {
    const val = bps[key];
    if (width >= val && val > max) {
      curBp = key;
      max = val;
    }
  });
  return curBp;
};

/**
 *
 * Ported from react-cool-dimensions
 * @link https://github.com/wellyshen/react-cool-dimensions
 * Copyright (c) 2020 Welly Shen

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in all
  copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
  SOFTWARE.
 */
const useDimensions = ({
  ref: refOpt,
  useBorderBoxSize,
  breakpoints,
  updateOnBreakpointChange,
  shouldUpdate,
  onResize,
  polyfill
} = {}) => {
  const _useState = (0, _react.useState)({
      currentBreakpoint: '',
      width: 0,
      x: 0,
      height: 0
    }),
    state = _useState[0],
    setState = _useState[1];
  const prevSizeRef = (0, _react.useRef)({});
  const prevBreakpointRef = (0, _react.useRef)();
  const observerRef = (0, _react.useRef)(null);
  const onResizeRef = (0, _react.useRef)(null);
  const shouldUpdateRef = (0, _react.useRef)(null);
  const warnedRef = (0, _react.useRef)(false);
  const refVar = (0, _react.useRef)(null);
  let ref = (0, _react.useRef)(refVar === null || refVar === void 0 ? void 0 : refVar.current);
  ref = refOpt !== null && refOpt !== void 0 ? refOpt : ref;
  (0, _react.useEffect)(() => {
    if (onResize) onResizeRef.current = onResize;
  }, [onResize]);
  (0, _react.useEffect)(() => {
    if (shouldUpdate) shouldUpdateRef.current = shouldUpdate;
  }, [shouldUpdate]);
  const observe = (0, _react.useCallback)(element => {
    if (element) ref.current = element;
    if (observerRef.current && ref.current) {
      observerRef.current.observe(ref.current);
    }
  }, [ref]);
  const unobserve = (0, _react.useCallback)(() => {
    if (observerRef.current) observerRef.current.disconnect();
  }, []);
  (0, _react.useEffect)(() => {
    var _getBrowserGlobals;
    if (!ref.current) return () => null;
    const window = (_getBrowserGlobals = (0, _browser.getBrowserGlobals)()) === null || _getBrowserGlobals === void 0 ? void 0 : _getBrowserGlobals.window;
    if (!window || (!('ResizeObserver' in window) || !('ResizeObserverEntry' in window)) && !polyfill) {
      // eslint-disable-next-line no-console
      console.error(observerErr);
      return () => null;
    }
    const Observer = polyfill || window.ResizeObserver;
    observerRef.current = new Observer(([entry]) => {
      var _ref$current$getBound, _ref$current, _ref$current$getBound2;
      const contentBoxSize = entry.contentBoxSize,
        borderBoxSize = entry.borderBoxSize,
        contentRect = entry.contentRect;
      let boxSize = contentBoxSize;
      if (useBorderBoxSize) {
        if (borderBoxSize) {
          boxSize = borderBoxSize;
        } else if (!warnedRef.current) {
          // eslint-disable-next-line no-console
          console.warn(borderBoxWarn);
          warnedRef.current = true;
        }
      }

      // @juggle/resize-observer polyfill has different data structure
      const entrySize = Array.isArray(boxSize) ? boxSize[0] : boxSize;
      const width = entrySize ? entrySize.inlineSize : contentRect.width;
      const height = entrySize ? entrySize.blockSize : contentRect.height;
      if (width === prevSizeRef.current.width && height === prevSizeRef.current.height) return;
      const x = (_ref$current$getBound = (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : (_ref$current$getBound2 = _ref$current.getBoundingClientRect()) === null || _ref$current$getBound2 === void 0 ? void 0 : _ref$current$getBound2.x) !== null && _ref$current$getBound !== void 0 ? _ref$current$getBound : 0;
      prevSizeRef.current = {
        width,
        height,
        x
      };
      const config = {
        currentBreakpoint: '',
        width,
        height,
        x,
        entry,
        observe,
        unobserve
      };
      if (breakpoints) {
        config.currentBreakpoint = getCurrentBreakpoint(breakpoints, width);
        if (config.currentBreakpoint !== prevBreakpointRef.current) {
          if (onResizeRef.current) onResizeRef.current(config);
          prevBreakpointRef.current = config.currentBreakpoint;
        }
      } else if (onResizeRef.current) {
        onResizeRef.current(config);
      }
      const next = {
        currentBreakpoint: config.currentBreakpoint,
        width,
        height,
        x,
        entry
      };
      if (shouldUpdateRef.current && !shouldUpdateRef.current(next)) return;
      if (!shouldUpdateRef.current && breakpoints && updateOnBreakpointChange) {
        setState(prev => prev.currentBreakpoint !== next.currentBreakpoint ? next : prev);
        return;
      }
      setState(next);
    });
    observe(null);
    return () => unobserve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(breakpoints), useBorderBoxSize, observe, unobserve, updateOnBreakpointChange]);
  return Object.assign({
    ref
  }, state, {
    observe,
    unobserve
  });
};
exports.useDimensions = useDimensions;